import { useState, useEffect } from "react";
import axios from "axios";
import { 
  Shield, RefreshCw, Copy, Check, Lock, Users, Flame, Server, Eye,
  Terminal, Wifi, AlertTriangle, CheckCircle, XCircle
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const auditTypes = [
  { value: "full", label: "Full Security Audit", icon: Shield, description: "Complete system security assessment" },
  { value: "permissions", label: "File Permissions", icon: Lock, description: "Check file and directory permissions" },
  { value: "firewall", label: "Firewall Rules", icon: Flame, description: "Analyze firewall configuration" },
  { value: "users", label: "User Accounts", icon: Users, description: "Review user accounts and privileges" },
  { value: "services", label: "Running Services", icon: Server, description: "Audit active services and ports" },
];

const statusIcon = (status) => {
  if (status === "error") return <XCircle className="w-4 h-4 text-red-400 flex-shrink-0" />;
  if (status === "warning") return <AlertTriangle className="w-4 h-4 text-yellow-400 flex-shrink-0" />;
  return <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0" />;
};

export default function SecurityAudit() {
  const [servers, setServers] = useState([]);
  const [selectedServer, setSelectedServer] = useState("");
  const [auditType, setAuditType] = useState("full");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copiedId, setCopiedId] = useState(null);

  useEffect(() => {
    axios.get(`${API}/servers`).then(res => setServers(res.data)).catch(() => {});
  }, []);

  const sshServers = servers.filter(s => s.ssh_configured);
  const selectedServerObj = servers.find(s => s.id === selectedServer);

  const runLiveAudit = async () => {
    if (!selectedServer) {
      toast.error("Select a server first");
      return;
    }
    setLoading(true);
    setResults(null);
    try {
      const res = await axios.post(`${API}/security-audit/live?server_id=${selectedServer}&audit_type=${auditType}`);
      setResults(res.data);
      toast.success(`Audit complete on ${res.data.server_name}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Audit failed — check SSH credentials");
    } finally {
      setLoading(false);
    }
  };

  const copyOutput = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    toast.success("Copied");
    setTimeout(() => setCopiedId(null), 2000);
  };

  return (
    <div className="p-6 space-y-6" data-testid="security-audit-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold font-mono text-white tracking-tight">Security Audit</h1>
          <p className="text-zinc-400 text-sm mt-1">Run real security checks on your servers via SSH</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Panel — Config */}
        <div className="space-y-4">
          {/* Server Selection */}
          <Card className="bg-[#0c0c0e] border-white/5">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg font-medium text-white flex items-center gap-2">
                <Terminal className="w-5 h-5 text-blue-400" />
                Target Server
              </CardTitle>
            </CardHeader>
            <CardContent>
              {sshServers.length === 0 ? (
                <div className="text-sm text-zinc-500 py-2">
                  <p>No servers with SSH configured.</p>
                  <p className="mt-1">Go to <span className="text-blue-400">Servers</span> → Setup SSH first.</p>
                </div>
              ) : (
                <Select value={selectedServer} onValueChange={setSelectedServer}>
                  <SelectTrigger className="bg-black/50 border-white/10 text-white" data-testid="audit-server-select">
                    <SelectValue placeholder="Select a server" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0c0c0e] border-white/10">
                    {sshServers.map(s => (
                      <SelectItem key={s.id} value={s.id}>
                        <span className="flex items-center gap-2">
                          <Wifi className="w-3 h-3 text-green-400" />
                          {s.name} ({s.ip_address})
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              {selectedServerObj && (
                <div className="mt-3 flex gap-2">
                  <Badge className="text-xs bg-zinc-800 text-zinc-400 border-zinc-700">{selectedServerObj.os_type}</Badge>
                  <Badge className="text-xs bg-green-500/10 text-green-400 border-green-500/20">SSH Ready</Badge>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Audit Type Selection */}
          <Card className="bg-[#0c0c0e] border-white/5">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg font-medium text-white flex items-center gap-2">
                <Eye className="w-5 h-5 text-blue-400" />
                Audit Type
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {auditTypes.map((type) => (
                <button
                  key={type.value}
                  onClick={() => setAuditType(type.value)}
                  className={`w-full flex items-start gap-3 p-3 text-left rounded-lg border transition-colors ${
                    auditType === type.value
                      ? 'bg-blue-500/10 border-blue-500/30 text-white'
                      : 'bg-black/30 border-white/5 text-zinc-400 hover:border-white/10'
                  }`}
                  data-testid={`audit-type-${type.value}`}
                >
                  <type.icon className={`w-5 h-5 mt-0.5 ${auditType === type.value ? 'text-blue-400' : 'text-zinc-500'}`} />
                  <div>
                    <p className={`text-sm font-medium ${auditType === type.value ? 'text-white' : 'text-zinc-300'}`}>
                      {type.label}
                    </p>
                    <p className="text-xs text-zinc-500">{type.description}</p>
                  </div>
                </button>
              ))}
            </CardContent>
          </Card>

          <Button
            onClick={runLiveAudit}
            disabled={loading || !selectedServer}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white"
            data-testid="run-audit-btn"
          >
            {loading ? (
              <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Running Audit...</>
            ) : (
              <><Shield className="w-4 h-4 mr-2" /> Run Live Audit</>
            )}
          </Button>
        </div>

        {/* Right Panel — Results */}
        <div className="lg:col-span-2">
          <Card className="bg-[#0c0c0e] border-white/5 h-full">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg font-medium text-white flex items-center gap-2">
                <Shield className="w-5 h-5 text-green-400" />
                Audit Results
                {results && (
                  <span className="text-sm font-normal text-zinc-400">
                    — {results.server_name} ({results.os_type})
                  </span>
                )}
              </CardTitle>
              {results && (
                <Badge className="bg-blue-500/10 text-blue-400 border-blue-500/20 text-xs">
                  {results.results?.length || 0} checks
                </Badge>
              )}
            </CardHeader>
            <CardContent className="overflow-y-auto max-h-[calc(100vh-250px)]">
              {!results && !loading ? (
                <div className="h-[400px] flex flex-col items-center justify-center text-center">
                  <Shield className="w-12 h-12 text-zinc-600 mb-4" />
                  <p className="text-zinc-400 mb-2">Select a server and audit type</p>
                  <p className="text-zinc-500 text-sm">Live audit connects via SSH and runs real security checks</p>
                </div>
              ) : loading ? (
                <div className="h-[400px] flex flex-col items-center justify-center">
                  <RefreshCw className="w-8 h-8 text-blue-400 animate-spin mb-4" />
                  <p className="text-zinc-400">Running security audit via SSH...</p>
                  <p className="text-zinc-500 text-sm mt-1">This may take a minute</p>
                </div>
              ) : (
                <div className="space-y-3" data-testid="audit-results">
                  {results.results?.map((item, idx) => (
                    <div key={idx} className="border border-white/5 rounded-lg overflow-hidden" data-testid={`audit-check-${idx}`}>
                      <div className="flex items-center justify-between px-4 py-2.5 bg-black/30">
                        <div className="flex items-center gap-2">
                          {statusIcon(item.status)}
                          <span className="text-sm font-medium text-white">{item.check}</span>
                        </div>
                        <button
                          onClick={() => copyOutput(item.output, idx)}
                          className="p-1 rounded hover:bg-white/10 text-zinc-500 hover:text-white transition-colors"
                        >
                          {copiedId === idx ? <Check className="w-3.5 h-3.5 text-green-400" /> : <Copy className="w-3.5 h-3.5" />}
                        </button>
                      </div>
                      <div className="px-4 py-1 bg-black/10">
                        <code className="text-[10px] text-zinc-600 font-mono">{item.command}</code>
                      </div>
                      <pre className="px-4 py-3 bg-black/50 text-xs text-zinc-300 font-mono overflow-x-auto max-h-48 overflow-y-auto whitespace-pre-wrap">
                        {item.output}
                      </pre>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
